import React, { useState, useEffect } from 'react';
import { logoutUser } from '../repository/authRepository';
import { listConfigs, createConfig, updateConfig, deleteConfig, LinkCheckerConfig } from '../repository/linkCheckerRepository';
import ConfigForm from '../components/ConfigForm';
import { Link } from 'react-router-dom';

const LinkCheckerDashboard: React.FC = () => {
  const [configs, setConfigs] = useState<LinkCheckerConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<LinkCheckerConfig | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedConfigs = await listConfigs();
      setConfigs(fetchedConfigs);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch configurations.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logoutUser();
    window.location.href = '/login';
  };

  const handleAddConfigClick = () => {
    setEditingConfig(null);
    setShowConfigForm(true);
    setFormError(null);
  };

  const handleEditConfigClick = (config: LinkCheckerConfig) => {
    setEditingConfig(config);
    setShowConfigForm(true);
    setFormError(null);
  };

  const handleFormSubmit = async (website_url: string, description: string) => {
    setFormLoading(true);
    setFormError(null);
    try {
      if (editingConfig) {
        await updateConfig(editingConfig.config_uuid, website_url, description);
      } else {
        await createConfig(website_url, description);
      }
      setShowConfigForm(false);
      setEditingConfig(null);
      fetchConfigs();
    } catch (err: any) {
      setFormError(err.message || 'Failed to save configuration.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteConfig = async (config_uuid: string) => {
    if (window.confirm('Are you sure you want to delete this configuration?')) {
      setLoading(true);
      setError(null);
      try {
        await deleteConfig(config_uuid);
        fetchConfigs();
      } catch (err: any) {
        setError(err.message || 'Failed to delete configuration.');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="app-shell">
      <header className="app-header">
        <h1>LinkChecker</h1>
        <button className="button ghost" onClick={handleLogout}>Logout</button>
      </header>
      <main className="app-content">
        {error && <div className="alert error">{error}</div>}

        <div className="section-header">
          <h2>Your Configurations</h2>
          {!loading && !showConfigForm && (
            <button className="button" onClick={handleAddConfigClick}>Add New</button>
          )}
        </div>

        {loading && <p>Loading configurations...</p>}

        {showConfigForm && (
          <div className="card" style={{ marginBottom: 'var(--space-lg)' }}>
            <h3>{editingConfig ? 'Edit Configuration' : 'Add New Configuration'}</h3>
            <ConfigForm
              initialConfig={editingConfig || undefined}
              onSubmit={handleFormSubmit}
              onCancel={() => {
                setShowConfigForm(false);
                setEditingConfig(null);
                setFormError(null);
              }}
              isLoading={formLoading}
              error={formError}
            />
          </div>
        )}

        {!loading && configs.length === 0 && !showConfigForm && (
          <div className="empty-state">
            <p>No configurations yet. Add your first website to check!</p>
          </div>
        )}

        {!loading && configs.length > 0 && !showConfigForm && (
          <div className="config-list">
            {configs.map((config) => (
              <div className="config-row" key={config.config_uuid}>
                <div className="config-row-info">
                  <strong>{config.description}</strong>
                  <span>{config.website_url}</span>
                </div>
                <div className="config-row-actions">
                  <Link className="button small ghost" to={`/config/${config.config_uuid}/runs`}>View Runs</Link>
                  <button className="button small secondary" onClick={() => handleEditConfigClick(config)}>Edit</button>
                  <button className="button small danger" onClick={() => handleDeleteConfig(config.config_uuid)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default LinkCheckerDashboard;
